import {Code, ConnectError} from "@connectrpc/connect";
import {firebaseIdentityChanged} from "../auth/firebaseIdentityBinding";
import TermsStorage, {type AcceptanceRecord} from "./TermsStorage";

/**
 * What the client replays so an account holder's acceptance becomes a persisted legal record: the Terms
 * version the click accepted (`./acceptedTermsVersion`, kitecorp/transferify-ui#3485) and the instant of the
 * click.
 */
export interface TermsAcceptancePayload {
	version: string;
	acceptedAt: string;
}

export type TermsAcceptanceReplayOutcome =
	| "nothing-to-replay"
	| "recorded"
	| "discarded"
	| "retryable-failure";

export type SubmitTermsAcceptance = (
	accountId: string,
	payload: TermsAcceptancePayload,
) => Promise<void>;

export type VerifyTermsAcceptanceAccount = (accountId: string) => Promise<void>;

const inFlight = new Map<string, Promise<TermsAcceptanceReplayOutcome>>();

/**
 * The replayable acceptance for this identity, or null.
 *
 * An unclaimed click has no server identity. This is deliberately a non-mutating peek: ownership moves
 * only after Firebase verifies the exact account. A recorded acceptance remains local to keep that
 * account's gate closed, but no longer produces another request on later renders or loads.
 */
export function pendingTermsAcceptance(accountId: string | null = null): TermsAcceptancePayload | null {
	const record = accountId === null
		? TermsStorage.retrieveRecord()
		: TermsStorage.pendingForReplay(accountId);
	if (record === null || record.status !== "pending") {
		return null;
	}
	return payloadOf(record);
}

/**
 * One account/version/timestamp replay funnel for login, same-session acceptance and store changes.
 * Concurrent triggers join the same promise, successful records become durable local successes, and a
 * refusal of the request itself (`invalid_argument`) discards the bad browser claim so the gate reopens silently
 * (owner ruling, 2026-08-22). Ownership moves only after Firebase verifies the account; an identity change before the
 * request returns a newly moved claim to the unclaimed slot with the account that clicked, if any (api#780),
 * while transport and server failures retain verified account ownership for a later retry.
 */
export async function replayTermsAcceptance(
	accountId: string | null,
	submit: SubmitTermsAcceptance,
	verify: VerifyTermsAcceptanceAccount,
): Promise<TermsAcceptanceReplayOutcome> {
	// An unversioned click (stored before every click named a version) is still claimed below, like any
	// signed-out click, so the next account to sign in on this browser is asked for its own; only one this
	// account already holds is skipped here.
	if (accountId === null || TermsStorage.pendingForReplay(accountId) === null) {
		return "nothing-to-replay";
	}
	try {
		await verify(accountId);
	} catch {
		return "retryable-failure";
	}
	return replayVerifiedTermsAcceptance(accountId, submit);
}

function replayVerifiedTermsAcceptance(
	accountId: string,
	submit: SubmitTermsAcceptance,
): Promise<TermsAcceptanceReplayOutcome> {
	const claim = TermsStorage.claimForReplay(accountId);
	if (claim === null || claim.acceptance.status !== "pending") {
		return Promise.resolve("nothing-to-replay");
	}
	const {acceptance} = claim;
	// A click stored before every click named a version (`./acceptedTermsVersion`). Kept rather than
	// discarded: it is still the gate's answer for a person who did click, and discarding it would ask them
	// again mid-send. It simply never becomes a record naming Terms nobody chose for it.
	if (acceptance.version === null) {
		return Promise.resolve("nothing-to-replay");
	}
	const payload = payloadOf(acceptance);
	if (payload === null) {
		TermsStorage.discard(accountId, acceptance);
		return Promise.resolve("discarded");
	}
	const key = replayKey(accountId, acceptance);
	const current = inFlight.get(key);
	if (current !== undefined) {
		return current;
	}
	const run = (async (): Promise<TermsAcceptanceReplayOutcome> => {
		try {
			await submit(accountId, payload);
			TermsStorage.markRecorded(accountId, acceptance);
			return "recorded";
		} catch (error) {
			if (firebaseIdentityChanged(error)) {
				TermsStorage.releaseClaim(accountId, claim);
				return "retryable-failure";
			}
			if (refusedRequest(error)) {
				TermsStorage.discard(accountId, acceptance);
				return "discarded";
			}
			return "retryable-failure";
		}
	})();
	inFlight.set(key, run);
	void run.finally(() => {
		if (inFlight.get(key) === run) {
			inFlight.delete(key);
		}
	});
	return run;
}

function replayKey(accountId: string, acceptance: AcceptanceRecord): string {
	return JSON.stringify([accountId, acceptance.version, acceptance.acceptedAt]);
}

/**
 * Whether the platform refused the request itself. The code decides, never the HTTP status: a switched-off module
 * (`failed_precondition`) is answered with 400 too, and that claim is worth another try.
 */
function refusedRequest(error: unknown): boolean {
	return error instanceof ConnectError && error.code === Code.InvalidArgument;
}

/**
 * Normalize offset-bearing instants while preserving the exact moment the visitor accepted. Null for a
 * click stored before every click named a version, which has nothing to record.
 */
function payloadOf(record: AcceptanceRecord): TermsAcceptancePayload | null {
	const parsed = Date.parse(record.acceptedAt);
	if (record.version === null || Number.isNaN(parsed)) {
		return null;
	}
	return {version: record.version, acceptedAt: new Date(parsed).toISOString()};
}
